import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { ContextUsage, Message } from '../components/Pleri/store/chatStore';

export enum PleriConversationStatus {
  Thinking = 'thinking',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
  Stopped = 'stopped',
}
// Types
export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  metadata?: {
    toolCalls?: unknown[];
    toolResults?: unknown[];
    [key: string]: unknown;
  };
}

export interface Conversation {
  id: string;
  scope: string;
  tenantId: string;
  organizationId: string;
  messages: Message[];
  contextUsage?: ContextUsage;
  status: PleriConversationStatus;
  /** The single-conversation endpoint nests the row here; fetchConversationFromDb lifts status out. */
  conversation?: { status: PleriConversationStatus };
  createdAt: string;
  lastUpdated: string;
  statusUpdatedAt: string;
  isHidden?: boolean;
  totalTokens?: number | null;
  meta?: {
    [key: string]: unknown;
  };
}

// Add ConversationListItem type for the list view
export interface ConversationListItem {
  conversationId: string;
  title?: string;
  source?: string;
  scope?: string;
  createdAt: string;
  updatedAt: string;
  statusUpdatedAt?: string;
  messageCount: number;
  externalId?: string;
  sourceId?: string;
  status: PleriConversationStatus;
  followUpNotify?: boolean;
  pinned?: boolean;
  hasErrors?: boolean;
  totalTokens?: number | null;
}

// Add ListConversationsParams for query parameters
export interface ListConversationsParams {
  source?: string;
  scope?: string;
  limit?: number;
  offset?: number;
  sourceId?: string;
  spotlightSummaryId?: string;
  followUpNotify?: boolean;
  pinned?: boolean;
  meta?: unknown;
  isHidden?: boolean | 'all';
}

// Add ListConversationsResponse type
export interface ListConversationsResponse {
  data: { conversations: ConversationListItem[] };
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// API paths
const CONVERSATIONS_PATH = '/api/core/pleri/conversations';

// Query keys
export const conversationKeys = {
  all: ['conversations'] as const,
  list: (params?: ListConversationsParams) =>
    [...conversationKeys.all, 'list', params] as const,
  detail: (id: string) => [...conversationKeys.all, 'detail', id] as const,
};

// Hooks
export const useListConversations = (
  params?: ListConversationsParams,
  enabled?: boolean,
) => {
  const { data, ...rest } = useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.source) {
        queryParams.append('source', params.source);
      }
      if (params?.scope) {
        queryParams.append('scope', params.scope);
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.offset) {
        queryParams.append('offset', params.offset.toString());
      }
      if (params?.sourceId) {
        queryParams.append('sourceId', params.sourceId);
      }
      if (params?.spotlightSummaryId) {
        queryParams.append('spotlightSummaryId', params.spotlightSummaryId);
      }
      if (params?.followUpNotify !== undefined) {
        queryParams.append('followUpNotify', params.followUpNotify.toString());
      }
      if (params?.pinned !== undefined) {
        queryParams.append('pinned', params.pinned.toString());
      }
      if (params?.isHidden !== undefined) {
        queryParams.append('isHidden', params.isHidden.toString());
      }
      const url = `${CONVERSATIONS_PATH}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const { data } = await api.get<ListConversationsResponse>(url);

      return {
        conversations: data.data.conversations.map((conversation) => ({
          ...conversation,
          status: getStatusText(conversation),
        })),
      };
    },
    enabled: enabled ?? true,
  });
  return { data, ...rest };
};

export const useGetConversation = (conversationId: string) => {
  const { data: conversation, ...rest } = useQuery({
    queryKey: conversationKeys.detail(conversationId),
    queryFn: async () => {
      const { data } = await api.get<{ data: Conversation }>(
        `${CONVERSATIONS_PATH}/${conversationId}`,
      );
      return {
        ...data.data,
        status: getStatusText(data.data),
      };
    },
    enabled: !!conversationId,
  });

  return { data: conversation, ...rest };
};

// Mark follow-up as read
export const useMarkFollowUpAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await api.put(`${CONVERSATIONS_PATH}/${conversationId}/follow-up/read`);
    },
    onSuccess: (_, conversationId) => {
      // Invalidate conversation list queries to refresh followUpNotify status
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      // Also invalidate the specific conversation detail
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
  });
};

// Delete conversation
export const useDeleteConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await api.delete(`${CONVERSATIONS_PATH}/${conversationId}`);
    },
    onSuccess: (_data, conversationId) => {
      // Invalidate all conversation-related queries so lists refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      // Also invalidate detail cache for the deleted id
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
  });
};

// Pin conversation
export const usePinConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      pinned,
    }: {
      conversationId: string;
      pinned: boolean;
    }): Promise<void> => {
      await api.put(`${CONVERSATIONS_PATH}/${conversationId}`, { pinned });
    },
    onSuccess: (_data, { conversationId }) => {
      // Invalidate all conversation-related queries so lists refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      // Also invalidate detail cache for the updated conversation
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
  });
};

// Rename conversation
export const useRenameConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      title,
    }: {
      conversationId: string;
      title: string;
    }): Promise<void> => {
      await api.put(`${CONVERSATIONS_PATH}/${conversationId}`, { title });
    },
    onSuccess: (_data, { conversationId }) => {
      // Invalidate all conversation-related queries so lists refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      // Also invalidate detail cache for the updated conversation
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
  });
};

// Stop a running conversation
export const stopConversation = (conversationId: string, messageId?: string) =>
  api.post(`${CONVERSATIONS_PATH}/${conversationId}/stop`, { messageId });

export const getStatusText = (conversation: {
  status: PleriConversationStatus;
  hasErrors?: boolean;
}): PleriConversationStatus => {
  if (
    conversation.status === PleriConversationStatus.Completed &&
    conversation.hasErrors
  ) {
    return PleriConversationStatus.Failed;
  }
  return conversation.status;
};
